import React, { useEffect, useState } from 'react';
import { Card, CardStandardHeader, CardFooterBase } from "@coherence-design-system/controls";
import { Stack, Text, Spinner, SpinnerSize } from '@fluentui/react';
import { DateFormatDropdown } from '../../../components/DateFormat/DateFormatDropdown';
import { getDefaultDateFormatForCountry, getCountryDateFormat } from '../../../Utils/DateFormatUtility';
import { useLocalStorageContext } from '../../../Provider/LocalStorageProvider';
import { UserRoles } from '../../../common/enum/UserRoles';

interface DateFormatConfigCardProps {
  countryName?: string;
  isEditMode?: boolean;
  onEditToggle?: () => void;
  onDateFormatChange?: (format: string) => void;
  initialDateFormat?: string;
}

export const DateFormatConfigCard: React.FC<DateFormatConfigCardProps> = ({ 
  countryName,
  isEditMode = false,
  onEditToggle,
  onDateFormatChange,
  initialDateFormat
}) => {
  const { dateFormat, setUser, userRole } = useLocalStorageContext();
  const [apiDateFormat, setApiDateFormat] = useState<string | null>(initialDateFormat || null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Use initialDateFormat from props if provided
  useEffect(() => {
    if (initialDateFormat) {
      setApiDateFormat(initialDateFormat);
      // Update context with the date format from backend
      setUser(prevUser => ({
        ...prevUser,
        dateFormat: initialDateFormat
      }));
      console.log(`Using date format from backend API: ${initialDateFormat}`);
    }
  }, [initialDateFormat, setUser]);
  
  // Fetch date format from API when country changes or on initial load
  useEffect(() => {
    const fetchDateFormat = async () => {
      if (countryName && !initialDateFormat) {
        setIsLoading(true);
        try {
          // Try to fetch from API first
          const response = await getCountryDateFormat(countryName);
          
          if (response) {
            console.log(`Fetched date format from API for ${countryName}: ${response}`);
            setApiDateFormat(response);
            
            // Update context
            setUser(prevUser => ({
              ...prevUser,
              dateFormat: response
            }));
          } else {
            // If API didn't return anything, fall back to default country format
            const preferredFormat = getDefaultDateFormatForCountry(countryName);
            console.log(`No API value available for ${countryName}, using default format: ${preferredFormat}`);
            
            setApiDateFormat(preferredFormat);
            setUser(prevUser => ({
              ...prevUser,
              dateFormat: preferredFormat
            }));
          }
        } catch (error) {
          console.error(`Error fetching date format for ${countryName}:`, error);
          // Fall back to default if API call fails
          const preferredFormat = getDefaultDateFormatForCountry(countryName);
          setApiDateFormat(preferredFormat);
          setUser(prevUser => ({
            ...prevUser,
            dateFormat: preferredFormat
          }));
          
          // Log error to console instead of using trackException
          console.error("Error fetching date format in DateFormatConfigCard.tsx:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    // Only fetch if we don't already have initialDateFormat from props
    if (!initialDateFormat && countryName) {
      fetchDateFormat();
    }
  }, [countryName, initialDateFormat, setUser]);

  const handleFormatChange = (format: string) => {
    console.log(`Date format changed to: ${format}`);
    
    // Update our local state for the API value
    setApiDateFormat(format);
    
    // Update context with the new format
    setUser(prevUser => ({
      ...prevUser,
      dateFormat: format
    }));
    
    // Notify parent component about the format change to update database
    if (onDateFormatChange) {
      onDateFormatChange(format);
    }
  };

  // Check if we're using the country's default format
  // We're using the default if:
  // 1. We have a country name, AND
  // 2. The API value equals the country's preferred format OR we don't have an API value
  const isUsingCountryDefault = countryName && 
    (apiDateFormat === getDefaultDateFormatForCountry(countryName) || !apiDateFormat);
  
  const preferredFormat = countryName ? getDefaultDateFormatForCountry(countryName) : '';

  return (
    <Card 
      header={<CardStandardHeader cardTitle="Date Format Configuration" />}
      footer={
        // Only show footer with edit button if not already in edit mode and if onEditToggle is provided
        !isEditMode && onEditToggle ? (
          <Stack style={{ position: "relative" }}>
            <div
              style={{
                width: "100%",
                height: "1px",
                backgroundColor: "rgb(225, 223, 221)",
                marginBottom: "10px",
              }}
            ></div>
            {userRole === UserRoles.Admin && (
              <CardFooterBase
                styles={{ root: { marginLeft: "12px" } }}
                links={[
                  {
                    onClick: onEditToggle,
                    children: <>Edit details</>,
                    underline: false,
                  },
                ]}
              />
            )}
          </Stack>
        ) : undefined
      }
    >
      <Stack style={{ padding: '10px' }}>
        {isLoading ? (
          <Stack horizontal horizontalAlign="center" style={{ padding: '20px' }}>
            <Spinner size={SpinnerSize.medium} label="Loading date format..." />
          </Stack>
        ) : isEditMode ? (
          <DateFormatDropdown 
            label="Date Format" 
            onChange={handleFormatChange}
            initialValue={apiDateFormat || undefined}
          />
        ) : (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 3 }} style={{ marginBottom: '10px' }}>
            <Text style={{ fontWeight: 600, minWidth: 150 }}>Current Date Format :</Text>
            <Text style={{ fontSize: '16px' }}>
              {apiDateFormat || 
               (countryName ? getDefaultDateFormatForCountry(countryName) : 'DD-MM-YYYY')}
            </Text>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};